import type { DatabaseAppUsagePort } from '@blocksuite/affine/blocks/database';
import type { BlockTrashGuard } from '@blocksuite/affine-block-streamlain';

import { readDatabaseAppUsageOnce } from '../../../../modules/streamlain/native-app/database-app-usage-port';
import type { DatabaseDeleteConfirmRequest } from '../../../../modules/streamlain/services/database-delete-confirm';
import {
  type DatabaseDeletionService,
  databaseLocalVisibility,
} from '../../../../modules/streamlain/services/database-deletion';

/**
 * The trash guard of one workspace. A cloud table is confirmed with the apps
 * that read it and then queued for the server, so the host doc changes only
 * by the receipt's sync; a table nobody reads hides meanwhile, one in use or
 * of unknown usage stays visible. A local workspace has no server and no
 * apps: it confirms and trashes in the doc.
 */
export function createDatabaseTrashGuard({
  local,
  port,
  confirm,
  docTitle,
  requestTrash,
}: {
  local: boolean;
  port: DatabaseAppUsagePort | undefined;
  confirm: (request: DatabaseDeleteConfirmRequest) => Promise<boolean>;
  docTitle: (docId: string) => string;
  requestTrash: DatabaseDeletionService['requestTrash'];
}): BlockTrashGuard<'database'> {
  return async ({ store, blockId, identity, displayName }) => {
    const titles = { databaseTitle: displayName, docTitle: docTitle(store.id) };
    if (local) {
      const confirmed = await confirm({
        action: 'trash',
        ...titles,
        appUsage: null,
      });
      return confirmed ? 'trash-locally' : 'cancelled';
    }
    const appUsage = await readDatabaseAppUsageOnce(port, identity.databaseId);
    if (!(await confirm({ action: 'trash', ...titles, appUsage }))) {
      return 'cancelled';
    }
    await requestTrash(
      { docId: store.id, blockId, databaseId: identity.databaseId },
      databaseLocalVisibility(appUsage)
    );
    return 'handed-to-server';
  };
}
